import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, ArrowRight, Coins, Repeat, ChevronDown, Check, CreditCard } from 'lucide-react';
import { PRICING_DATA, Tier } from './PricingSection';
import { UsageBillingB } from './billing/UsageBillingB';

// ── Billing modal ────────────────────────────────────────────────────────────
// A self-contained "Add credits" / manage-subscription flow, modeled on the
// canonical Stripe-style credit purchase pattern but rebranded to Giga's warm
// espresso / cream / orange language (light surface so it inherits the
// dashboard's dark-mode overrides automatically).
//
//   choose    → pick a one-time top-up vs a recurring monthly plan
//   buy       → one-time credit amount + one-time-charge summary
//   subscribe → monthly plan amount + current → new comparison
// ─────────────────────────────────────────────────────────────────────────────

type Step = 'choose' | 'buy' | 'subscribe';
type CreditOption = {
  credits: number;
  price: number;
};
const CREDIT_OPTIONS: CreditOption[] = [{
  credits: 8000,
  price: 25
}, {
  credits: 16000,
  price: 50
}, {
  credits: 24000,
  price: 75
}, {
  credits: 32000,
  price: 100
}, {
  credits: 48000,
  price: 150
}, {
  credits: 64000,
  price: 200
}, {
  credits: 80000,
  price: 250
}, {
  credits: 160000,
  price: 500
}, {
  credits: 240000,
  price: 750
}, {
  credits: 320000,
  price: 1000
}, {
  credits: 400000,
  price: 1250
}, {
  credits: 480000,
  price: 1500
}, {
  credits: 640000,
  price: 2000
}, {
  credits: 800000,
  price: 2500
}, {
  credits: 1600000,
  price: 5000
}, {
  credits: 2400000,
  price: 7500
}, {
  credits: 3200000,
  price: 10000
}];

// Monthly plans reuse the pricing page's tier list verbatim (PRICING_DATA),
// including its group headings, so the modal and /pricing always agree.
const MONTHLY_TIERS = PRICING_DATA;
// Trial state — trialing workspaces run on the 20,000-credit monthly plan for
// 7 days at $0, then convert to that paid tier unless changed here. The
// picker therefore defaults to the trial's own tier.
const TRIAL_TIER = MONTHLY_TIERS.find((i): i is Tier => 'credits' in i && i.credits === 20000) as Tier;
const TRIAL_DAYS_LEFT = 4;
// Size of the current workspace — gives the plan picker context on how much
// usage the team is choosing credits for.
const WORKSPACE_PEOPLE = 18;
// Usage — how many of the trial's monthly credits are still available.
const CREDITS_AVAILABLE = 7600;
const CREDITS_TOTAL = TRIAL_TIER.credits;
// Compact "7.6k" formatting for the usage headline.
const fmtCompact = (n: number) => n >= 1000 ? `${parseFloat((n / 1000).toFixed(1))}k` : String(n);
const fmt = (n: number) => n.toLocaleString('en-US');

// A lightweight styled <select> shell that matches the espresso form language.
function SelectShell({
  children,
  value,
  onChange
}: {
  children: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
}) {
  return <div className="relative" data-id="element-1910" data-mp="2jlyuqwh6kyj6">
      <select value={value} onChange={e => onChange(e.target.value)} className="w-full appearance-none rounded-xl border border-neutral-300 bg-white px-4 py-3 pr-11 text-[15px] font-medium text-[#2B211A] outline-none transition-shadow focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/25" data-id="element-1911" data-mp="2jourwil7fj6k">
        
        {children}
      </select>
      <ChevronDown size={18} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-1912" data-mp="20ws9p2swzuu7" />
      
    </div>;
}
export function BillingModal({
  open,
  onClose,
  initialVariant = 'A'
}: {
  open: boolean;
  onClose: () => void;
  /** Which billing model to open on. The starter-credit product uses 'B'. */
  initialVariant?: 'A' | 'B';
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('choose');
  // Debug switch for comparing the two billing models side by side:
  //   A — today's trial that converts to a monthly plan
  //   B — no trial; $100 of starter credit, subscribe once it is spent
  const [variant, setVariant] = useState<'A' | 'B'>(initialVariant);
  const [creditPrice, setCreditPrice] = useState('25');
  // Selected monthly tier + the grouped tier-picker popover (pricing-page
  // style). No default — the trial tier is already the "Current" plan, so the
  // user has to pick a (different) package before continuing.
  const [selectedTier, setSelectedTier] = useState<Tier | null>(null);
  const [tierOpen, setTierOpen] = useState(false);
  const tierRef = useRef<HTMLDivElement>(null);
  // Hand off to the Stripe-style checkout page, opened directly on the
  // payment step.
  const goToPayment = () => {
    onClose();
    navigate('/app-screen-mockups/trial', {
      state: {
        step: 'checkout'
      }
    });
  };

  // Reset back to the chooser each time the modal is reopened.
  useEffect(() => {
    if (open) {
      setStep('choose');
      setTierOpen(false);
      setSelectedTier(null);
    }
  }, [open]);

  // Close the tier picker on outside-click.
  useEffect(() => {
    if (!tierOpen) return;
    const onDown = (e: MouseEvent) => {
      if (tierRef.current && !tierRef.current.contains(e.target as Node)) {
        setTierOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [tierOpen]);

  // Escape to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const selectedCredit = CREDIT_OPTIONS.find(o => String(o.price) === creditPrice) ?? CREDIT_OPTIONS[0];
  return <AnimatePresence data-id="element-1913" data-mp="39qgt6ks1g0bb">
      {open && <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.18
    }} role="dialog" aria-modal="true" aria-label="Usage and billing" data-id="element-1914" data-mp="39r9u354bfvlj">
        
          {/* Backdrop */}
          <div className="absolute inset-0 bg-[#2B211A]/50 backdrop-blur-sm" onClick={onClose} data-id="element-1915" data-mp="33ai11egwset0" />
        

          {/* Panel */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 28
      }} className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_40px_100px_-30px_rgba(43,33,26,0.55)]" data-id="element-1916" data-mp="2yc6rz5yfu8bu">
          
            {/* Header */}
            <div className="flex items-center justify-between border-b border-neutral-100 px-6 py-5" data-id="element-1917" data-mp="il0z2tcbvybi">
              <h2 className="text-lg font-bold text-[#2B211A]" data-id="element-1918" data-mp="drzhgdkqlko8">
                {step === 'choose' ? 'Usage & billing' : 'Add credits'}
              </h2>
              <div className="flex items-center gap-3" data-id="element-1919" data-mp="q3iaj53rln74">
                {step === 'choose' && <div className="flex items-center rounded-lg bg-neutral-100 p-0.5" role="group" aria-label="Billing model variant" data-id="element-1920" data-mp="q5laohymswpk">
                    {(['A', 'B'] as const).map(v => <button key={v} type="button" onClick={() => setVariant(v)} aria-pressed={variant === v} className={`rounded-md px-3 py-1 text-[13px] font-bold transition-colors ${variant === v ? 'bg-white text-[#2B211A] shadow-sm' : 'text-neutral-400 hover:text-neutral-600'}`} data-id="element-1921" data-mp="103iyj4ckwrhn">
                        {v}
                      </button>)}
                  </div>}
                <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-[#2B211A]" data-id="element-1922" data-mp="1isp9g574sesw">
                  <X size={20} data-id="element-1923" data-mp="1sqdgnulxbery" />
                </button>
              </div>
            </div>

            <div className="max-h-[calc(100vh-14rem)] overflow-y-auto px-6 py-6" data-id="element-1924" data-mp="2e1k5qzu4cc9w">
              <AnimatePresence mode="wait" data-id="element-1925" data-mp="2e3ep49pqrwbv">
                {/* ── Chooser ─────────────────────────────────────────────── */}
                {step === 'choose' && <motion.div key="choose" initial={{
              opacity: 0,
              x: -8
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -8
            }} transition={{
              duration: 0.18
            }} data-id="element-1926" data-mp="2n177jmqrfk3h">
                    {variant === 'B' ? <UsageBillingB data-id="element-1927" data-mp="1qdu1vemk2cws" /> : <>
                    {/* Usage — remaining credits this cycle */}
                    <div className="rounded-xl border border-neutral-200 p-5" data-id="element-1928" data-mp="245h2trrg09lw">
                      <div className="flex items-baseline justify-between gap-4" data-id="element-1929" data-mp="1zb4dj13ac428">
                        <p className="text-[15px] font-semibold text-[#2B211A]" data-id="element-1930" data-mp="1bls9efmtem6s">
                          Total credits available
                        </p>
                        <p className="text-[17px] font-bold text-[#2B211A]" data-id="element-1931" data-mp="3iblvlrth0519">
                          {fmtCompact(CREDITS_AVAILABLE)}
                        </p>
                      </div>
                      <div role="progressbar" aria-valuenow={CREDITS_AVAILABLE} aria-valuemin={0} aria-valuemax={CREDITS_TOTAL} aria-label="Credits available this cycle" className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100" data-id="element-1932" data-mp="39anpn3zh1ng1">
                    
                        <div className="h-full rounded-full bg-[#EA580C]" style={{
                      width: `${Math.round(CREDITS_AVAILABLE / CREDITS_TOTAL * 100)}%`
                    }} data-id="element-1933" data-mp="5ugob44j42af" />
                    
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-4" data-id="element-1934" data-mp="ultiphmusk3">
                        <p className="text-[13px] text-neutral-500" data-id="element-1935" data-mp="2glzxqpk7vzti">
                          of {fmt(CREDITS_TOTAL)} monthly credits · Trial ·{' '}
                          {TRIAL_DAYS_LEFT} days left
                        </p>
                        <p className="shrink-0 text-[13px] text-neutral-500" data-id="element-1936" data-mp="1oobvr477ewrk">
                          ${fmt(TRIAL_TIER.price)}/mo after trial
                        </p>
                      </div>
                    </div>

                    {/* Payment method + invoices — managed in Stripe */}
                    <div className="mt-3 flex items-center justify-between gap-4 rounded-xl border border-neutral-200 px-5 py-3.5" data-id="element-1937" data-mp="1jx3oadpfplh1">
                      <span className="flex items-center gap-3 text-[13px] text-neutral-500" data-id="element-1938" data-mp="15wj69jrcftol">
                        <CreditCard size={16} className="shrink-0 text-neutral-400" data-id="element-1939" data-mp="1a190l46ct07e" />
                        Payment method{' '}
                        <span className="font-medium text-[#2B211A]" data-id="element-1940" data-mp="w29d3zukurct">
                          Visa ··4242
                        </span>
                      </span>
                      <button type="button" className="shrink-0 text-[13px] font-semibold text-[#EA580C] transition-colors hover:text-[#c2470a]" data-id="element-1941" data-mp="2qeh1euce89zm">
                    
                        Manage in Stripe
                      </button>
                    </div>

                    <h3 className="mt-6 text-[17px] font-semibold text-[#2B211A]" data-id="element-1942" data-mp="er3zsexb08bm">
                      Choose how to add credits
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500" data-id="element-1943" data-mp="thl4c758jecq">
                      Pick a one-time top-up or manage your monthly plan.
                    </p>

                    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-1944" data-mp="12evetqa8azgl">
                      {/* Buy credits */}
                      <button type="button" onClick={() => setStep('buy')} className="group flex flex-col rounded-xl border border-neutral-200 p-5 text-left transition-all hover:border-[#EA580C] hover:shadow-[0_16px_40px_-20px_rgba(234,88,12,0.5)]" data-id="element-1945" data-mp="xgtyhd46te8t">
                    
                        <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[#EA580C]/10 text-[#EA580C]" data-id="element-1946" data-mp="3e6r0h18gs7qc">
                          <Coins size={22} data-id="element-1947" data-mp="398ph0f74m6ec" />
                        </span>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-1948" data-mp="2qyoqjyuf9di">
                          One-time
                        </span>
                        <span className="mt-1 text-lg font-bold text-[#2B211A]" data-id="element-1949" data-mp="bpsryt6oiy01">
                          Buy credits
                        </span>
                        <span className="mt-1.5 text-sm leading-relaxed text-neutral-500" data-id="element-1950" data-mp="gn2g85ic13ma">
                          A one-time charge, added instantly.
                        </span>
                      </button>

                      {/* Subscribe monthly */}
                      <button type="button" onClick={() => setStep('subscribe')} className="group flex flex-col rounded-xl border border-neutral-200 p-5 text-left transition-all hover:border-[#EA580C] hover:shadow-[0_16px_40px_-20px_rgba(234,88,12,0.5)]" data-id="element-1951" data-mp="uhldy3nbziy9">
                    
                        <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[#EA580C]/10 text-[#EA580C]" data-id="element-1952" data-mp="19daxlv51njzt">
                          <Repeat size={22} data-id="element-1953" data-mp="2txg5ze8ufobe" />
                        </span>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-1954" data-mp="2ovqs7qu8kwjm">
                          Recurring
                        </span>
                        <span className="mt-1 text-lg font-bold text-[#2B211A]" data-id="element-1955" data-mp="2eyui5goow2cy">
                          Manage subscription
                        </span>
                        <span className="mt-1.5 text-sm leading-relaxed text-neutral-500" data-id="element-1956" data-mp="3cll2c0n68706">
                          Adjust your monthly credit plan and save.
                        </span>
                      </button>
                    </div>
                    </>}
                  </motion.div>}

                {/* ── Buy credits (one-time) ──────────────────────────────── */}
                {step === 'buy' && <motion.div key="buy" initial={{
              opacity: 0,
              x: 8
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: 8
            }} transition={{
              duration: 0.18
            }} data-id="element-1957" data-mp="3ve5iqiloojn3">
                
                    <button type="button" onClick={() => setStep('choose')} className="mb-6 flex items-center gap-2 text-sm font-medium text-neutral-500 transition-colors hover:text-[#2B211A]" data-id="element-1958" data-mp="315yciw88zky7">
                  
                      <ArrowLeft size={16} data-id="element-1959" data-mp="3tu2i7hqtjy0w" />
                      Back
                    </button>

                    <label className="block text-sm font-semibold text-[#2B211A]" data-id="element-1960" data-mp="2oueab9q5vxx">
                      Credit amount
                    </label>
                    <div className="mt-2" data-id="element-1961" data-mp="3k4iyk9va804z">
                      <SelectShell value={creditPrice} onChange={setCreditPrice} data-id="element-1962" data-mp="3p3m744ah0ohp">
                        {CREDIT_OPTIONS.map(o => <option key={o.price} value={o.price} data-id="element-1963" data-mp="gnk91k92njj4">
                            {fmt(o.credits)} credits — ${fmt(o.price)}
                          </option>)}
                      </SelectShell>
                    </div>

                    {/* One-time charge summary */}
                    <div className="mt-4 rounded-xl border border-neutral-200 bg-[#FAF8F4] p-5" data-id="element-1964" data-mp="2164o1h2amayf">
                      <div className="flex items-start justify-between gap-4" data-id="element-1965" data-mp="2fv0z7mznc439">
                        <div data-id="element-1966" data-mp="2fx3sqh00cfcq">
                          <p className="text-[15px] font-semibold text-[#2B211A]" data-id="element-1967" data-mp="2aved0o1ubdib">
                            One-time charge
                          </p>
                          <p className="mt-0.5 text-sm text-neutral-500" data-id="element-1968" data-mp="1n6lmj73zlxv3">
                            {fmt(selectedCredit.credits)} credits added to your
                            account
                          </p>
                        </div>
                        <span className="text-2xl font-bold text-[#EA580C]" data-id="element-1969" data-mp="1s4wwdhua88db">
                          ${fmt(selectedCredit.price)}
                        </span>
                      </div>
                      <div className="mt-4 space-y-1.5 border-t border-neutral-200 pt-4 text-[13px] text-neutral-500" data-id="element-1970" data-mp="33l4t32n18ph2">
                        <p data-id="element-1971" data-mp="208mm3aj64ylv">Valid for 1 year from purchase</p>
                        <p data-id="element-1972" data-mp="258sif40dv9aa">Used only after all other credits are consumed</p>
                      </div>
                    </div>

                    <p className="mt-4 text-center text-sm text-neutral-500" data-id="element-1973" data-mp="2a7lyn4721ee5">
                      Subscribe and save with a{' '}
                      <button type="button" onClick={() => setStep('subscribe')} className="font-semibold text-[#EA580C] underline underline-offset-2 hover:text-[#c2470a]" data-id="element-1974" data-mp="2e8xfolbv4raq">
                    
                        monthly plan
                      </button>
                    </p>

                    <button type="button" onClick={goToPayment} className="mt-6 w-full rounded-xl bg-[#EA580C] py-3.5 text-[15px] font-bold text-white shadow-[0_12px_30px_-10px_rgba(234,88,12,0.6)] transition-colors hover:bg-[#d24e08]" data-id="element-1975" data-mp="1qj33m5zdb6rj">
                  
                      Continue to payment
                    </button>
                  </motion.div>}

                {/* ── Subscribe monthly (recurring) ───────────────────────── */}
                {step === 'subscribe' && <motion.div key="subscribe" initial={{
              opacity: 0,
              x: 8
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: 8
            }} transition={{
              duration: 0.18
            }} data-id="element-1976" data-mp="qb6e0vkyz8vs">
                
                    <button type="button" onClick={() => setStep('choose')} className="mb-6 flex items-center gap-2 text-sm font-medium text-neutral-500 transition-colors hover:text-[#2B211A]" data-id="element-1977" data-mp="ccot15mg8ahk">
                  
                      <ArrowLeft size={16} data-id="element-1978" data-mp="2mn8gntb1a7e" />
                      Back
                    </button>

                    <label className="block text-sm font-semibold text-[#2B211A]" data-id="element-1979" data-mp="3urr1k7s34qic">
                      Monthly amount
                    </label>
                    <p className="mt-0.5 text-[13px] text-neutral-500" data-id="element-1980" data-mp="3kr6vmrtmexts">
                      Shared credits for your {WORKSPACE_PEOPLE}-person
                      workspace.
                    </p>
                    {/* Grouped tier picker — the same listbox as the pricing
                     page, with group headings per company size. */}
                    <div className="relative mt-2" ref={tierRef} data-id="element-1981" data-mp="13hu9zg2n6aj1">
                      <button type="button" onClick={() => setTierOpen(o => !o)} aria-haspopup="listbox" aria-expanded={tierOpen} aria-label="Select monthly credit tier" className="flex w-full items-center justify-between rounded-xl border border-neutral-300 bg-white px-4 py-3 text-left transition-colors hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#EA580C]/25" data-id="element-1982" data-mp="18gnl51ughpg1">
                    
                        {selectedTier ? <span className="text-[15px] font-medium text-[#2B211A]" data-id="element-1983" data-mp="otzb1hy1j9xi">
                            {fmt(selectedTier.credits)} credits monthly
                          </span> : <span className="text-[15px] font-medium text-neutral-400" data-id="element-1984" data-mp="ft170znlr2ex">
                            Select a credit package
                          </span>}
                        <span className="flex items-center gap-2.5" data-id="element-1985" data-mp="3ndaim0c702t2">
                          {selectedTier && <span className="text-[15px] font-medium text-neutral-500" data-id="element-1986" data-mp="3nfn9j2zxhni9">
                              ${fmt(selectedTier.price)}
                            </span>}
                          <ChevronDown size={18} className={`text-neutral-400 transition-transform duration-200 ${tierOpen ? 'rotate-180' : ''}`} data-id="element-1987" data-mp="14re5birvcc9" />
                      
                        </span>
                      </button>

                      <AnimatePresence data-id="element-1988" data-mp="ovncyslnb7t0">
                        {tierOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -8
                  }} transition={{
                    duration: 0.15
                  }} role="listbox" aria-label="Monthly credit tiers" className="absolute left-0 right-0 top-full z-20 mt-2 max-h-[300px] overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-2 shadow-2xl" data-id="element-1989" data-mp="ouc1e6lykvrc">
                      
                            {MONTHLY_TIERS.map((item, idx) => {
                      if ('group' in item) {
                        return <div key={`group-${idx}`} className="px-4 pt-5 pb-2 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-1990" data-mp="1afl0ikndtuqe">
                              
                                    {item.group}
                                  </div>;
                      }
                      const isSelected = item.credits === selectedTier?.credits;
                      // The trial's 20,000-credit tier is what the
                      // workspace is already on — shown but not
                      // selectable as a "new" plan.
                      const isCurrent = item.credits === TRIAL_TIER.credits;
                      return <button key={item.credits} type="button" role="option" aria-selected={isSelected} disabled={isCurrent} onClick={() => {
                        setSelectedTier(item);
                        setTierOpen(false);
                      }} className={`flex w-full items-center justify-between rounded-xl px-4 py-2.5 text-left transition-colors ${isSelected ? 'bg-[#FCF3EC] text-[#EA580C]' : isCurrent ? 'cursor-default text-neutral-400' : 'text-[#2B211A] hover:bg-neutral-50'}`} data-id="element-1991" data-mp="2an9nqx3aa5p7" data-mpi={idx}>
                            
                                  <span className="flex items-center gap-3" data-id="element-1992" data-mp="2ybsvnbif8ggr">
                                    <span className="flex w-5 justify-center" data-id="element-1993" data-mp="2kdksgluskix5">
                                      {isSelected && <Check size={16} strokeWidth={3} data-id="element-1994" data-mp="2pbw32yswuxgf" />}
                                    </span>
                                    <span className="text-sm font-medium" data-id="element-1995" data-mp="18xmo2vak6l29">
                                      {fmt(item.credits)} credits monthly
                                    </span>
                                  </span>
                                  <span className="flex items-center gap-3" data-id="element-1996" data-mp="tucxuid39t2i">
                                    {isCurrent && <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold text-neutral-500" data-id="element-1997" data-mp="ptszjbgs4jes">
                                        Current plan
                                      </span>}
                                    {item.popular && <span className="rounded-full bg-[#FBEAE0] px-2.5 py-0.5 text-xs font-semibold text-[#EA580C]" data-id="element-1998" data-mp="b44ymbcifmrl">
                                        Most popular
                                      </span>}
                                    <span className={`text-sm font-medium ${isSelected ? 'text-[#EA580C]' : 'text-neutral-500'}`} data-id="element-1999" data-mp="24q676cs924g">
                                
                                      ${fmt(item.price)}
                                    </span>
                                  </span>
                                </button>;
                    })}
                          </motion.div>}
                      </AnimatePresence>
                    </div>

                    {/* Current → New comparison — the trial runs on the
                     20,000-credit plan at its real price, struck through and
                     free until the trial ends. */}
                    <div className="mt-4 flex items-stretch gap-3" data-id="element-2000" data-mp="2dqse6es55cf2">
                      <div className="flex-1 rounded-xl border border-neutral-200 p-4" data-id="element-2001" data-mp="2dt4rufqv90mb">
                        <div className="flex items-center justify-between gap-2" data-id="element-2002" data-mp="1g54gzrnv2q2h">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-2003" data-mp="1l2wkzk1s62n1">
                            Current
                          </p>
                          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-500" data-id="element-2004" data-mp="1u3b7unue7psn">
                            Trial · {TRIAL_DAYS_LEFT} days left
                          </span>
                        </div>
                        <p className="mt-1.5 text-2xl font-bold text-neutral-400 line-through decoration-2" data-id="element-2005" data-mp="35kavv79pjigv">
                          ${fmt(TRIAL_TIER.price)}{' '}
                          <span className="text-sm font-medium text-neutral-400" data-id="element-2006" data-mp="2z39eryktg4la">
                            / month
                          </span>
                        </p>
                        <p className="mt-1 text-sm text-neutral-500" data-id="element-2007" data-mp="38zvyul5birm9">
                          {fmt(TRIAL_TIER.credits)} credits / month · Free
                          until your trial ends
                        </p>
                      </div>
                      <div className="flex items-center text-neutral-400" data-id="element-2008" data-mp="3hyr7s4zqyft9">
                        <ArrowRight size={18} data-id="element-2009" data-mp="jsryhm156yz" />
                      </div>
                      {selectedTier ? <div className="flex-1 rounded-xl border border-[#EA580C]/40 bg-[#EA580C]/5 p-4" data-id="element-2010" data-mp="3rry9nfva3dol">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-[#EA580C]" data-id="element-2011" data-mp="9ljwhj4u37r9">
                            New
                          </p>
                          <p className="mt-1.5 text-2xl font-bold text-[#EA580C]" data-id="element-2012" data-mp="o77kxgi2kyfx">
                            ${fmt(selectedTier.price)}{' '}
                            <span className="text-sm font-medium text-[#EA580C]/70" data-id="element-2013" data-mp="t60wh3dsqfdg">
                              / month
                            </span>
                          </p>
                          <p className="mt-1 text-sm text-neutral-600" data-id="element-2014" data-mp="k4klouj2kb8p">
                            {fmt(selectedTier.credits)} credits / month
                          </p>
                        </div> : <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-neutral-300 p-4" data-id="element-2015" data-mp="a8zpjd0om56v">
                          <p className="text-center text-sm font-medium text-neutral-400" data-id="element-2016" data-mp="3sjujvrju8wb7">
                            Select a credit package
                          </p>
                        </div>}
                    </div>

                    <p className="mt-4 text-[13px] leading-relaxed text-neutral-500" data-id="element-2017" data-mp="3nmum03x56zv6">
                      Monthly credits expire at the end of each billing cycle,
                      and downgrades are scheduled for the end of the billing
                      cycle. You&rsquo;ll be redirected to Stripe to review and
                      confirm.
                    </p>

                    <button type="button" onClick={goToPayment} disabled={!selectedTier} className="mt-6 w-full rounded-xl bg-[#EA580C] py-3.5 text-[15px] font-bold text-white shadow-[0_12px_30px_-10px_rgba(234,88,12,0.6)] transition-colors hover:bg-[#d24e08] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none" data-id="element-2018" data-mp="1kc7r4xz3sw6o">
                  
                      Continue to payment
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}